import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http, inr } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Eyebrow } from "../components/Reveal";

const FIELDS = [
  ["name", "Full name", "text", "md:col-span-2"],
  ["email", "Email", "email", ""],
  ["phone", "Phone", "tel", ""],
  ["address", "Address", "text", "md:col-span-2"],
  ["city", "City", "text", ""],
  ["state", "State", "text", ""],
  ["pincode", "Pincode", "text", ""],
];

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function Checkout() {
  const { items, subtotal, shipping, total, clear } = useCart();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", pincode: "" });
  const [method, setMethod] = useState("online");
  const [busy, setBusy] = useState(false);

  const pay = async (order) => {
    if (!(await loadRazorpay())) { toast.error("Couldn't load Razorpay. Try again."); return; }
    const rz = new window.Razorpay({
      key: order.razorpay_key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.razorpay_order_id,
      name: "T Brand",
      description: "Drop 01 order",
      prefill: { name: form.name, email: form.email, contact: form.phone },
      theme: { color: "#00FF66" },
      handler: async (res) => {
        try {
          await http.post(`/orders/${order.order_id}/verify`, res);
          clear();
          nav(`/order/${order.order_id}`);
        } catch {
          toast.error("Payment verification failed.");
        }
      },
      modal: { ondismiss: () => setBusy(false) },
    });
    rz.open();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/orders", { items: items.map(({ slug, size, qty }) => ({ slug, size, qty })), customer: form, payment_method: method });
      if (data.payment_method === "online" && data.payments_enabled) { await pay(data); return; }
      clear();
      nav(`/order/${data.order_id}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Check your details: valid email, 10-digit phone, 6-digit pincode.");
      setBusy(false);
    }
  };

  if (!items.length) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 px-5 text-center" data-testid="checkout-empty">
      <p className="font-display text-6xl font-black uppercase">Bag's empty</p>
      <Link to="/" className="bg-[#00FF66] px-6 py-3 font-display text-xl font-black uppercase text-black">Shop drop 01</Link>
    </div>
  );

  return (
    <section className="px-5 pb-24 pt-28 md:px-12 md:pt-32" data-testid="checkout-page">
      <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-12">
        <form onSubmit={submit} className="lg:col-span-7" data-testid="checkout-form">
          <Eyebrow>Step 01 · Shipping</Eyebrow>
          <h1 className="mt-4 font-display text-6xl font-black uppercase leading-none md:text-7xl">Checkout</h1>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {FIELDS.map(([k, label, type, span]) => (
              <label key={k} className={`block ${span}`}>
                <span className="font-mono text-xs uppercase tracking-widest text-zinc-400">{label}</span>
                <input
                  data-testid={`checkout-${k}-input`}
                  required
                  type={type}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-2 w-full border border-white/15 bg-[#121215] px-4 py-3.5 outline-none transition-colors focus:border-[#00FF66]"
                />
              </label>
            ))}
          </div>
          <p className="mt-10 font-mono text-xs uppercase tracking-widest text-zinc-400">Payment method</p>
          <div className="mt-3 grid gap-3" data-testid="payment-method-picker">
            {[
              ["online", "Pay online", "UPI · Cards · Netbanking via Razorpay"],
              ["cod", "Cash on delivery", `Pay ${inr(total)} in cash when your order arrives`],
            ].map(([val, label, sub]) => (
              <button
                key={val}
                type="button"
                data-testid={`payment-method-${val}`}
                onClick={() => setMethod(val)}
                className={`flex items-start gap-3 border p-4 text-left transition-colors ${method === val ? "border-[#00FF66] bg-[#00FF66]/5" : "border-white/15 hover:border-white/40"}`}
              >
                <span className={`mt-1 grid h-4 w-4 shrink-0 place-items-center rounded-full border ${method === val ? "border-[#00FF66]" : "border-white/40"}`}>
                  {method === val && <span className="h-2 w-2 rounded-full bg-[#00FF66]" />}
                </span>
                <span>
                  <span className="block font-mono text-sm font-bold uppercase tracking-widest">{label}</span>
                  <span className="mt-1 block text-sm text-zinc-400">{sub}</span>
                </span>
              </button>
            ))}
          </div>
          <button data-testid="checkout-submit-btn" disabled={busy} className="mt-8 w-full bg-[#00FF66] py-5 font-display text-3xl font-black uppercase text-black transition-colors hover:bg-white disabled:opacity-50">
            {busy ? "Processing…" : method === "cod" ? `Place order · Pay ${inr(total)} on delivery` : `Pay ${inr(total)}`}
          </button>
          <p className="mt-3 font-mono text-xs uppercase text-zinc-400">{method === "cod" ? "Cash on delivery · India" : "Secure payments by Razorpay · UPI · Cards · Netbanking"}</p>
        </form>
        <aside className="h-fit border border-white/10 bg-[#121215] p-6 lg:col-span-5" data-testid="order-summary">
          <p className="font-display text-3xl font-black uppercase">Summary</p>
          <div className="mt-4 divide-y divide-white/10">
            {items.map((it) => (
              <div key={`${it.slug}-${it.size}`} className="flex gap-4 py-4">
                <img src={it.image} alt={it.name} className="h-20 w-16 object-cover" />
                <div className="flex-1">
                  <p className="font-display text-xl font-extrabold uppercase leading-none">{it.name}</p>
                  <p className="mt-1 font-mono text-xs uppercase text-zinc-400">Size {it.size} × {it.qty}</p>
                </div>
                <p className="font-mono text-sm">{inr(it.price * it.qty)}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 border-t border-white/10 pt-4 font-mono text-sm uppercase">
            <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            <div className="flex justify-between text-zinc-400"><span>Shipping</span><span>{shipping ? inr(shipping) : "Free"}</span></div>
            <div className="flex justify-between pt-2 text-lg font-bold"><span>Total</span><span data-testid="checkout-total">{inr(total)}</span></div>
          </div>
        </aside>
      </div>
    </section>
  );
}
